import type { ReactNode } from 'react';
import { Box, Button, Drawer, IconButton, Stack, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloseIcon from '@mui/icons-material/Close';
import InboxIcon from '@mui/icons-material/Inbox';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import SellOutlinedIcon from '@mui/icons-material/SellOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import type { InvestorListDTO, InvoiceTransactionDraftDTO } from '@inversor-hub/types';
import { formatCurrency } from '@/utils/misc';
import { transactionCategoryLabels } from '../constants';
import { formatReceived, investmentNameForDraft } from './utils';

interface InvoiceInboxDrawerProps {
  open: boolean;
  drafts: InvoiceTransactionDraftDTO[];
  investments?: InvestorListDTO[];
  reviewedCount: number;
  onClose: () => void;
  onReview: (draft: InvoiceTransactionDraftDTO) => void;
}

export const InvoiceInboxDrawer = ({
  open,
  drafts,
  reviewedCount,
  onClose,
  onReview,
}: InvoiceInboxDrawerProps) => (
  <Drawer
    anchor="right"
    open={open}
    onClose={onClose}
    PaperProps={{ sx: { width: 'min(480px, 94vw)', bgcolor: 'grey.50' } }}
  >
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box
        sx={{
          bgcolor: 'background.paper',
          px: 3,
          py: 2.25,
          borderBottom: 1,
          borderColor: 'grey.100',
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '11px',
                bgcolor: 'primary.50',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <InboxIcon sx={{ fontSize: 21, color: 'primary.main' }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: 18, fontWeight: 700, color: 'text.primary' }}>
                Bandeja de facturas
              </Typography>
              <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                {drafts.length} pendientes · {reviewedCount} revisadas
              </Typography>
            </Box>
          </Stack>
          <IconButton
            onClick={onClose}
            aria-label="Cerrar bandeja"
            sx={{ color: 'text.secondary' }}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', px: 2.25, py: 2 }}>
        {drafts.length === 0 ? (
          <EmptyInbox />
        ) : (
          <Stack spacing={1.5}>
            {drafts.map((draft) => (
              <InboxRow key={draft.id} draft={draft} onReview={() => onReview(draft)} />
            ))}
          </Stack>
        )}
      </Box>
    </Box>
  </Drawer>
);

const EmptyInbox = () => (
  <Box sx={{ textAlign: 'center', px: 2.5, py: '60px', color: 'text.secondary' }}>
    <Box
      sx={{
        width: 60,
        height: 60,
        borderRadius: '16px',
        bgcolor: 'success.50',
        color: 'success.main',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        mb: 1.75,
      }}
    >
      <DoneAllIcon sx={{ fontSize: 30 }} />
    </Box>
    <Typography sx={{ fontSize: 16, fontWeight: 600, color: 'text.primary' }}>
      Todo revisado
    </Typography>
    <Typography sx={{ fontSize: 13.5, mt: 0.6 }}>
      No tienes facturas pendientes. Las nuevas aparecerán aquí.
    </Typography>
  </Box>
);

interface InboxRowProps {
  draft: InvoiceTransactionDraftDTO;
  onReview: () => void;
}

const InboxRow = ({ draft, onReview }: InboxRowProps) => {
  const isEmail = draft.source === 'INBOUND_EMAIL';
  const investmentName = investmentNameForDraft(draft);

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        borderRadius: '14px',
        border: 1,
        borderColor: 'grey.100',
        p: 2,
        transition: 'box-shadow .18s',
        '&:hover': { boxShadow: (theme) => theme.palette.tokens.shadows.card },
      }}
    >
      <Stack direction="row" spacing={1.5}>
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: '10px',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: isEmail ? 'info.50' : (theme) => theme.palette.tokens.semantic.warningSurface,
          }}
        >
          {isEmail ? (
            <MailOutlineIcon sx={{ fontSize: 19, color: 'info.main' }} />
          ) : (
            <AttachFileIcon sx={{ fontSize: 19, color: 'accent.main' }} />
          )}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" justifyContent="space-between" spacing={1.25}>
            <Typography
              sx={{ fontSize: 14.5, fontWeight: 700, color: 'text.primary', minWidth: 0 }}
              noWrap
            >
              {draft.supplierName || draft.email?.subject || 'Factura recibida'}
            </Typography>
            <Typography
              sx={{
                fontSize: 15,
                fontWeight: 700,
                color: 'text.primary',
                whiteSpace: 'nowrap',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {draft.amount != null ? formatCurrency(draft.amount) : '—'}
            </Typography>
          </Stack>
          {draft.concept && (
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 0.25 }} noWrap>
              {draft.concept}
            </Typography>
          )}
          <Stack direction="row" spacing={0.9} flexWrap="wrap" useFlexGap sx={{ mt: 1.25 }}>
            <InboxTag icon={<SellOutlinedIcon sx={{ fontSize: 12 }} />}>
              {transactionCategoryLabels[draft.category]}
            </InboxTag>
            {investmentName ? (
              <InboxTag icon={<BusinessOutlinedIcon sx={{ fontSize: 12 }} />}>
                {investmentName}
              </InboxTag>
            ) : (
              <InboxTag tone="warning" icon={<HelpOutlineIcon sx={{ fontSize: 12 }} />}>
                Sin inversión asignada
              </InboxTag>
            )}
          </Stack>
        </Box>
      </Stack>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mt: 1.6, pt: 1.5, borderTop: 1, borderColor: 'grey.100' }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.6}
          sx={{ color: 'grey.300', minWidth: 0 }}
        >
          <AccessTimeIcon sx={{ fontSize: 12 }} />
          <Typography sx={{ fontSize: 11.5 }} noWrap>
            {formatReceived(draft)} ·{' '}
            {isEmail ? 'por email' : (draft.document?.fileName ?? 'archivo')}
          </Typography>
        </Stack>
        <Button
          size="small"
          variant={'contained'}
          endIcon={<ArrowForwardIcon />}
          onClick={onReview}
        >
          Revisar
        </Button>
      </Stack>
    </Box>
  );
};

interface InboxTagProps {
  children: ReactNode;
  icon: ReactNode;
  tone?: 'neutral' | 'warning';
}

const InboxTag = ({ children, icon, tone = 'neutral' }: InboxTagProps) => {
  const isWarning = tone === 'warning';

  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.6,
        fontSize: 12,
        fontWeight: 500,
        px: 1.1,
        py: 0.5,
        borderRadius: '7px',
        border: 1,
        borderColor: isWarning
          ? (theme) => theme.palette.tokens.semantic.warningBorder
          : 'grey.100',
        bgcolor: isWarning ? (theme) => theme.palette.tokens.semantic.warningSurface : 'grey.50',
        color: isWarning ? (theme) => theme.palette.tokens.semantic.warningText : 'text.secondary',
        maxWidth: '100%',
        minWidth: 0,
      }}
    >
      {icon}
      <Box
        component="span"
        sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
      >
        {children}
      </Box>
    </Box>
  );
};
